import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {AccessFormValidationSchema, CreateAccessFormValidationSchema} from '../../formSchemas'
import {TKey, useTranslation} from '@/translations/i18n'
import {InputSelectProjectByPatient} from '@/features/projects/components/input-select-project-by-patient/InputSelectProjectByPatient'
import {InputSelectUser} from '@/features/users/components/input-select-user/InputSelectUser'
import {InputSelectAccessType} from '@/features/access-types/components/input-select-access-type/InputSelectAccessType'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {projectSectorToLabel} from '@/features/projects/utils'
import {AccessFormSingleDayFields} from '../access-form-single-day-fields/AccessFormSingleDayFields'
import {AccessFormMonthlySumaryFields} from '../access-form-monthly-summary/AccessFormMonthlySumaryFields'
import {ObjectEntries} from '@/utilities/helpers'

const accessModeToLabel = {
    single_day: 'accesses:single_day',
    monthly_summary: 'accesses:monthly_summary'
} as const satisfies Record<AccessFormValidationSchema['accessMode'], TKey>

interface AccessFormProps {
    isEdit?: boolean
}

export const AccessForm = ({isEdit = false}: AccessFormProps) => {
    const {t} = useTranslation()
    const form = useFormContext<CreateAccessFormValidationSchema>()
    const patient = useWatch({control: form.control, name: 'patient'})
    const accessMode = useWatch({control: form.control, name: 'accessMode'})
    const consultant = useWatch({control: form.control, name: 'consultant'})

    const onChangePatientSideEffect = () => {
        form.resetField('project')
    }

    const onChangeAccessModeSideEffect = () => {
        form.resetField('date')
    }

    return (
        <Grid columns={2} columnGap={4} rowGap={6} fullWidth>
            <Controller
                control={form.control}
                name="patient"
                render={({field, fieldState}) => (
                    <InputSelectPatient
                        label={t('commons:patient')}
                        placeholder={t('agenda:select_patient')}
                        onChange={patient => {
                            field.onChange(patient)
                            onChangePatientSideEffect()
                        }}
                        value={field.value}
                        errorMessage={fieldState.error?.message}
                        disabled={isEdit}
                        hideBadge
                        hideDate
                    />
                )}
            />
            <Controller
                control={form.control}
                name="project"
                render={({field, fieldState}) => (
                    <InputSelectProjectByPatient
                        disabled={!patient || isEdit}
                        label={t('commons:project_number')}
                        placeholder={t('commons:select_a_project')}
                        onChange={field.onChange}
                        errorMessage={fieldState.error?.message}
                        patientId={patient?.id}
                        value={field.value}
                    />
                )}
            />

            <Controller
                control={form.control}
                name="consultant"
                render={({field, fieldState}) => (
                    <InputSelectUser
                        label={t('commons:consultant')}
                        placeholder={t('agenda:select_consultant')}
                        onChange={field.onChange}
                        errorMessage={fieldState.error?.message}
                        value={field.value}
                    />
                )}
            />

            <Controller
                control={form.control}
                name="accessType"
                render={({field, fieldState}) => (
                    <InputSelectAccessType
                        label={t('accesses:access_type')}
                        placeholder={t('agenda:select_access_type_placeholder')}
                        onChange={field.onChange}
                        disabled={!consultant}
                        errorMessage={fieldState.error?.message}
                        value={field.value}
                        specializationId={consultant?.specializationId ?? undefined}
                    />
                )}
            />
            <GridItem spanColumn={2}>
                <Controller
                    control={form.control}
                    name="sector"
                    render={({field, fieldState}) => (
                        <Flexbox direction="column" gap={1.5} fullWidth>
                            <Label>{t('accesses:mode')}</Label>
                            <Grid columns={2} columnGap={4} fullWidth>
                                {ObjectEntries(projectSectorToLabel)
                                    .filter(([value]) => ['outpatient', 'domiciliary'].includes(value))
                                    .map(([value, label]) => (
                                        <FormFieldBox
                                            key={value}
                                            isSelected={field.value === value}
                                            hasError={!!fieldState.error?.message}
                                        >
                                            <RadioField
                                                label={t(label)}
                                                name={field.name}
                                                checked={field.value === value}
                                                onChange={() => field.onChange(value)}
                                                value={value}
                                            />
                                        </FormFieldBox>
                                    ))}
                            </Grid>
                            {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
                        </Flexbox>
                    )}
                />
            </GridItem>
            {!isEdit && (
                <GridItem spanColumn={2}>
                    <Controller
                        control={form.control}
                        name="accessMode"
                        render={({field, fieldState}) => (
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>{t('accesses:access_mode')}</Label>
                                <Grid columns={2} columnGap={4} fullWidth>
                                    {Object.entries(accessModeToLabel).map(([value, label]) => (
                                        <FormFieldBox
                                            key={value}
                                            isSelected={field.value === value}
                                            hasError={!!fieldState.error?.message}
                                        >
                                            <RadioField
                                                label={t(label)}
                                                name={field.name}
                                                checked={field.value === value}
                                                onChange={() => {
                                                    field.onChange(value)
                                                    onChangeAccessModeSideEffect()
                                                }}
                                                value={value}
                                            />
                                        </FormFieldBox>
                                    ))}
                                </Grid>
                                {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
                            </Flexbox>
                        )}
                    />
                </GridItem>
            )}
            {accessMode === 'single_day' && (
                <GridItem spanColumn={2}>
                    <AccessFormSingleDayFields />
                </GridItem>
            )}
            {accessMode === 'monthly_summary' && (
                <GridItem spanColumn={2}>
                    <AccessFormMonthlySumaryFields />
                </GridItem>
            )}
        </Grid>
    )
}
